import React, { useState, useEffect, useRef } from "react";
import { useGetDistancesMutation } from "../store/locations/apis";
import { DriveTimeSegment } from "../types";

interface Waypoint {
  latitude: number;
  longitude: number;
  street: string;
  city: string;
  label: string;
}

const metersToMiles = (meters: number): number => {
  return meters * 0.000621371;
};

const secondsToMinutes = (seconds: number): number => {
  return seconds / 60;
};

const formatTotalDuration = (totalMinutes: number): string => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = Math.round(totalMinutes % 60);
  return hours >= 1 ? `${hours} hrs ${minutes} mins` : `${minutes} mins`;
};

interface DriveTimesProps {
  waypoints: Waypoint[];
  onChange?: (data: DriveTimeSegment[]) => void;
}

const DriveTimes: React.FC<DriveTimesProps> = ({ waypoints, onChange }) => {
  const [driveTimeData, setDriveTimeData] = useState<DriveTimeSegment[]>([]);
  const [getDistances] = useGetDistancesMutation();
  const [isLoading, setIsLoading] = useState(false);
  const lastWaypointsRef = useRef<string>("");

  useEffect(() => {
    const fetchDriveTimes = async () => {
      const validWaypoints = waypoints.filter(
        (wp) => wp?.latitude && wp?.longitude
      );

      // Add check for unchanged waypoints
      const waypointsString = JSON.stringify(validWaypoints);
      if (waypointsString === lastWaypointsRef.current) {
        return;
      }
      lastWaypointsRef.current = waypointsString;

      if (validWaypoints.length < 2) return;

      try {
        setIsLoading(true);
        const distances = await getDistances({
          coordinates: validWaypoints.map((wp) => ({
            latitude: wp.latitude,
            longitude: wp.longitude,
          })),
        });

        if (
          !distances?.data ||
          !Array.isArray(distances.data) ||
          distances.data.length === 0
        ) {
          console.error("Invalid distances response:", distances);
          return;
        }

        const segments = distances.data.map((segment, index) => ({
          from_label: validWaypoints[index]?.label || "",
          to_label: validWaypoints[index + 1]?.label || "",
          from_address: `${validWaypoints[index]?.street || ""}${
            validWaypoints[index]?.city ? `, ${validWaypoints[index].city}` : ""
          }`,
          to_address: `${validWaypoints[index + 1]?.street || ""}${
            validWaypoints[index + 1]?.city
              ? `, ${validWaypoints[index + 1].city}`
              : ""
          }`,
          distance: metersToMiles(segment.distance),
          duration: secondsToMinutes(segment.duration),
        }));

        console.log("segments", segments);
        setDriveTimeData(segments);
        onChange?.(segments);
      } catch (error) {
        console.error("Failed to fetch drive times:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDriveTimes();
  }, [waypoints, getDistances, onChange]);

  if (isLoading) {
    return (
      <div>
        <h3 className="text-lg font-semibold mb-6 text-slate-800">
          Drive Times
        </h3>
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }

  if (!driveTimeData.length) return null;

  return (
    <div className="text-sm">
      {/* <h3 className="text-lg font-semibold mb-6 text-slate-800">Drive Times</h3> */}

      {/* First address */}
      <div className="font-semibold">
        <span>{driveTimeData[0]?.from_label}</span>
      </div>

      {/* Middle segments and addresses */}
      {driveTimeData.map((segment, index) => (
        <div key={index}>
          <div className="my-2 flex items-center gap-2">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 14l-7 7m0 0l-7-7m7 7V3"
              />
            </svg>
            <span className="text-sm font-normal">
              {segment.distance.toFixed(1)} mi •{" "}
              {formatTotalDuration(segment.duration)}
            </span>
          </div>

          <div className="font-semibold">
            <span>{segment.to_label}</span>
          </div>
        </div>
      ))}

      <div className="mt-6 space-y-4 p-4 bg-stone-100">
        <div className="space-y-2">
          <div className="mb-1 font-bold">Round Trip</div>
          <div className="font-normal space-x-2">
            <span>
              {driveTimeData
                .reduce((acc, seg) => acc + seg.distance, 0)
                .toFixed(1)}{" "}
              miles
            </span>
            <span>•</span>
            <span>
              {formatTotalDuration(
                driveTimeData.reduce((acc, seg) => acc + seg.duration, 0)
              )}
            </span>
          </div>
        </div>

        <div className="space-y-2">
          <div className="mb-1 font-bold">Origin to Destination</div>
          <div className="font-normal space-x-2">
            <span>
              {driveTimeData
                .slice(1, -1)
                .reduce((acc, seg) => acc + seg.distance, 0)
                .toFixed(1)}{" "}
              miles
            </span>
            <span>•</span>
            <span>
              {formatTotalDuration(
                driveTimeData
                  .slice(1, -1)
                  .reduce((acc, seg) => acc + seg.duration, 0)
              )}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriveTimes;
